import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as RestartIcon } from '@/assets/icons/refresh-icon.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import NetworkIcon from '@/components/NetworkIcon';
import { ActionsWrap, Td, TopicsList } from '@/components/Table';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import ChannelFormModal from '@/pages/Settings/components/ChannelFormModal';
import { ChannelResource } from '@/types/resources';
import { api } from '@/utils/api';
import { numFormatter } from '@/utils/helpers';
import { hexToRgba } from '@/utils/theme';

import AuthorizationButton from './AuthorisationButton';

const Description = styled.div`
  display: flex;
  align-items: center;
  svg {
    position: absolute;
    top: -2px;
    left: -2px;
  }
`;

const DescriptionText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  * {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ImageWrap = styled.div`
  position: relative;
`;

const Image = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

const Notes = styled.div`
  width: 160px;
`;

const TestingBadge = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.purple, 0.15)};
  padding: 2px 8px;
  border-radius: 100px;
  display: inline-block;
  margin-top: 6px;
`;

const Tr = styled.tr<{ $red: boolean }>`
  ${({ $red, theme }) =>
    $red &&
    css`
      td,
      th {
        background: ${theme.colors.errorLight};
      }
    `}
`;

const StyledDropdownButton = styled(ActionsDropdown)<{ $width?: number }>`
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width}px;
    `}
`;

interface Props {
  channel: ChannelResource;
  activeMonth: string;
  isCompetitorChannel: boolean;
}

const ChannelsTableRow: FC<Props> = ({
  channel,
  activeMonth,
  isCompetitorChannel,
}) => {
  const [showEdit, setShowEdit] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const toasts = useToasts();

  const { teamId } = useParams();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to delete this ${
          channel.is_competitor ? 'competitor' : 'channel'
        }?`,
        channel.name,
      );

      if (!confirmed) return;

      const { id } = await api.delete<never, { id: number }>(
        `/v1/teams/${teamId}/channels/${channel.id}`,
      );

      queryClient.setQueryData(
        [
          'team_channels',
          {
            teamId,
            competitors: channel.is_competitor ? 1 : 0,
            month: activeMonth,
          },
        ],
        (channels: ChannelResource[] | undefined) =>
          channels!.filter((channel) => channel.id !== id),
      );

      alerts.success(
        `${
          channel.is_competitor ? 'Competitor' : 'Channel'
        } was successfully deleted`,
      );
    } catch (err) {
      alerts.error();
    }
  };

  const onRefetchIdentifier = async () => {
    const toastId = toasts.loading();

    try {
      const res = await api.post<never, { error?: string }>(
        `/v1/channels/${channel.id}/refetch-identifier`,
      );

      if (res.error) {
        alerts.error(res.error);
      } else {
        await queryClient.invalidateQueries([
          'team_channels',
          {
            teamId,
          },
        ]);
      }
    } catch {
      alerts.error();
    } finally {
      toasts.close(toastId);
    }
  };

  return (
    <Tr $red={!channel.authorized && !channel.is_competitor}>
      <Td>
        <Description>
          {!!channel.thumbnail && (
            <ImageWrap>
              <Image
                src={channel.thumbnail}
                alt={channel.name + ' thumbnail'}
                loading="lazy"
              />
              <NetworkIcon type={channel.network} />
            </ImageWrap>
          )}
          <DescriptionText>
            <Text $type="smallBold">{channel.name}</Text>
            <Text
              $type="extraSmall"
              $color="gray"
              as="a"
              href={channel.url}
              target="_blank"
            >
              {channel.url}
            </Text>
            {!channel.is_scrapable && (
              <TestingBadge>
                <Text $type="extraSmall" $color="purple">
                  Testing
                </Text>
              </TestingBadge>
            )}
          </DescriptionText>
        </Description>
      </Td>
      <Td>{channel.identifier ?? '-'}</Td>
      <Td>
        <TopicsList>
          {channel.tags!.map((tag) => (
            <Tag key={tag.id}>{tag.title}</Tag>
          ))}
        </TopicsList>
      </Td>
      <Td>{channel.subscribers ? numFormatter(channel.subscribers) : '-'}</Td>
      <Td>
        {channel.monthlyTotalViews
          ? numFormatter(channel.monthlyTotalViews)
          : '-'}
      </Td>
      <Td>
        {channel.monthlyPublishedViews
          ? numFormatter(channel.monthlyPublishedViews)
          : '-'}
      </Td>
      <Td>{channel.monthlyAvgV7 ? numFormatter(channel.monthlyAvgV7) : '-'}</Td>
      <Td>
        <Notes>{channel.notes}</Notes>
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <StyledDropdownButton
            $width={
              channel.network !== 'facebook' && !!!isCompetitorChannel
                ? 210
                : 200
            }
          >
            <AuthorizationButton
              channelId={channel.id}
              provider={channel.network === 'facebook' ? 'facebook' : 'google'}
              isCompetitorChannel={isCompetitorChannel}
            />
            <ActionsDropdownButton
              onClick={() => setShowEdit(true)}
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            {channel.network === 'facebook' && (
              <ActionsDropdownButton onClick={onRefetchIdentifier} as="button">
                <span>
                  <RestartIcon />
                </span>
                Refetch identifier
              </ActionsDropdownButton>
            )}
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </StyledDropdownButton>
          <AnimatePresence>
            {showEdit && (
              <ChannelFormModal
                activeMonth={activeMonth}
                onClose={() => setShowEdit(false)}
                competitor={channel.is_competitor}
                channel={channel}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default ChannelsTableRow;
